import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const COMPANY = {
  name: 'Swiss Chem International',
  address: '82-C Mehar Sadiq Market, Railway Road, Faisalabad',
  phone: '+92 300 7671404',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
};
const fmtMoney = (n) => 'Rs ' + Math.round(parseFloat(n) || 0).toLocaleString('en-PK');
const fmtQty = (n) => { const v = parseFloat(n) || 0; return v % 1 === 0 ? v.toLocaleString('en-PK') : v.toFixed(2); };

export const generateInvoiceExpensePdf = (detail, expenses, summary) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 12;
  const rightX = pageWidth - marginX;
  let y = 14;

  const lineAmount = parseFloat(detail.line_amount) || 0;
  const lineGst = parseFloat(detail.line_gst) || 0;
  const lineTotalWithGst = parseFloat(detail.line_total_with_gst) || 0;
  const cogs = parseFloat(detail.cogs) || 0;
  const qty = parseFloat(detail.quantity) || 0;
  const grandExpenses = parseFloat(summary.grand_total) || 0;
  const realMargin = lineAmount - cogs - grandExpenses;

  // ─── Header ───
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(COMPANY.name, marginX, y);

  doc.setFontSize(16);
  doc.text('Invoice Expense Report', pageWidth / 2, y, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(90, 90, 90);
  doc.text(COMPANY.address, rightX, y - 2, { align: 'right' });
  doc.text(`Ph: ${COMPANY.phone}`, rightX, y + 3, { align: 'right' });
  doc.text(`Printed: ${new Date().toLocaleString('en-GB')}`, rightX, y + 8, { align: 'right' });

  y += 13;
  doc.setDrawColor(160, 160, 160);
  doc.setLineWidth(0.3);
  doc.line(marginX, y, rightX, y);

  // ─── Invoice detail ───
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(26, 60, 110);
  doc.text(`Invoice: ${detail.invoice_no}`, marginX, y);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(40, 40, 40);
  doc.text(`Customer: ${detail.customer_name || '—'}`, rightX, y, { align: 'right' });

  y += 5;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, rightX, y);
  y += 5;

  const drawDetail = (label, value, x, yPos) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(label, x, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(30, 30, 30);
    doc.text(String(value), x, yPos + 4.5);
  };

  const col = (pageWidth - marginX * 2) / 4;
  drawDetail('Product', `${detail.product_code} — ${detail.product_name}`, marginX, y);
  drawDetail('Inv Date', fmtDate(detail.invoice_date), marginX + col, y);
  drawDetail('Qty', `${fmtQty(qty)} ${detail.unit || ''}`, marginX + col * 2, y);
  drawDetail('Rate', fmtMoney(detail.rate), marginX + col * 3, y);

  y += 12;
  drawDetail('Total (excl GST)', fmtMoney(lineAmount), marginX, y);
  drawDetail('GST', fmtMoney(lineGst), marginX + col, y);
  drawDetail('Total with GST', fmtMoney(lineTotalWithGst), marginX + col * 2, y);
  if (cogs > 0) drawDetail('Cost of Goods', fmtMoney(cogs), marginX + col * 3, y);

  y += 14;
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, y, rightX, y);
  y += 4;

  // ─── Expense table ───
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(26, 60, 110);
  doc.text('Expense Breakdown', marginX, y);
  y += 2;

  const byType = {};
  for (const ex of expenses) {
    const key = ex.expense_type_name || 'Other';
    if (!byType[key]) byType[key] = [];
    byType[key].push(ex);
  }

  const rows = [];
  for (const [typeName, items] of Object.entries(byType)) {
    for (const item of items) {
      rows.push([typeName, item.label || '—', fmtMoney(item.amount)]);
    }
  }

  autoTable(doc, {
    startY: y + 2,
    head: [['Category', 'Detail', 'Amount']],
    body: rows.length ? rows : [['—', 'No expenses recorded', '—']],
    theme: 'grid',
    headStyles: { fillColor: [235, 235, 235], textColor: [30, 30, 30], fontStyle: 'bold', fontSize: 8, lineColor: [160, 160, 160], lineWidth: 0.2 },
    bodyStyles: { fontSize: 8, textColor: [30, 30, 30], lineColor: [180, 180, 180], lineWidth: 0.15 },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 2) data.cell.styles.textColor = [192, 57, 43];
    },
    columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 'auto' }, 2: { cellWidth: 35, halign: 'right' } },
    margin: { left: marginX, right: marginX },
  });

  let afterY = doc.lastAutoTable.finalY + 4;

  // ─── Type totals ───
  if (summary.by_type && summary.by_type.length > 0) {
    doc.setDrawColor(40, 40, 40);
    doc.setLineWidth(0.3);
    doc.line(marginX, afterY, rightX, afterY);
    afterY += 5;

    for (const t of summary.by_type) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(t.expense_type_name || 'Other', marginX + 4, afterY);
      doc.text(fmtMoney(t.total), rightX - 4, afterY, { align: 'right' });
      afterY += 5;
    }

    doc.setLineWidth(0.4);
    doc.line(marginX, afterY, rightX, afterY);
    afterY += 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    doc.text('Total Expenses', marginX + 4, afterY);
    doc.setTextColor(192, 57, 43);
    doc.text(fmtMoney(grandExpenses), rightX - 4, afterY, { align: 'right' });
    afterY += 8;
  }

  // ─── Real Margin ───
  doc.setDrawColor(40, 40, 40);
  doc.setLineWidth(0.5);
  doc.line(marginX, afterY, rightX, afterY);
  afterY += 6;

  const summaryItems = [
    { label: 'Revenue (excl GST)', value: fmtMoney(lineAmount), color: [39, 174, 96] },
    { label: 'Cost of Goods', value: cogs > 0 ? `(${fmtMoney(cogs)})` : '—', color: [192, 57, 43] },
    { label: 'Expenses', value: `(${fmtMoney(grandExpenses)})`, color: [192, 57, 43] },
    { label: 'Real Margin', value: fmtMoney(realMargin), color: realMargin >= 0 ? [39, 174, 96] : [192, 57, 43] },
  ];

  const sumColW = (rightX - marginX) / summaryItems.length;
  summaryItems.forEach((s, i) => {
    const sx = marginX + i * sumColW;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(100, 100, 100);
    doc.text(s.label, sx + sumColW / 2, afterY, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(...s.color);
    doc.text(s.value, sx + sumColW / 2, afterY + 6, { align: 'center' });
  });

  // ─── Footer ───
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text('CONFIDENTIAL — For internal use only', pageWidth / 2, pageHeight - 6, { align: 'center' });
  doc.text('Page 1 of 1', rightX, pageHeight - 6, { align: 'right' });

  doc.save(`InvoiceExpense_${(detail.invoice_no || 'report').replace(/\s+/g, '_')}.pdf`);
};